import React, { useState } from 'react';

import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const FilterPanel = ({ isOpen, onClose, filters, onFiltersChange }) => {
  const [localFilters, setLocalFilters] = useState(filters);

  const claimTypeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'health', label: 'Health & Medicine' },
    { value: 'politics', label: 'Politics & Government' },
    { value: 'climate', label: 'Climate & Environment' },
    { value: 'economy', label: 'Economy & Finance' },
    { value: 'technology', label: 'Technology & Science' },
    { value: 'social', label: 'Social Issues' }
  ];

  const verdictStatusOptions = [
    { value: 'all', label: 'All Verdicts' },
    { value: 'true', label: 'True' },
    { value: 'mostly-true', label: 'Mostly True' },
    { value: 'mixed', label: 'Mixed' },
    { value: 'mostly-false', label: 'Mostly False' },
    { value: 'false', label: 'False' },
    { value: 'unverified', label: 'Unverified' }
  ];

  const countryOptions = [
    { value: 'all', label: 'All EU Countries' },
    { value: 'de', label: 'Germany' },
    { value: 'fr', label: 'France' },
    { value: 'it', label: 'Italy' },
    { value: 'es', label: 'Spain' },
    { value: 'pl', label: 'Poland' },
    { value: 'nl', label: 'Netherlands' },
    { value: 'be', label: 'Belgium' },
    { value: 'at', label: 'Austria' },
    { value: 'se', label: 'Sweden' },
    { value: 'dk', label: 'Denmark' }
  ];

  const dateRangeOptions = [
    { value: 'all', label: 'All Time' },
    { value: '24h', label: 'Last 24 Hours' },
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 3 Months' },
    { value: '1y', label: 'Last Year' }
  ];

  const handleFilterChange = (key, value) => {
    const updatedFilters = { ...localFilters, [key]: value };
    setLocalFilters(updatedFilters);
  };

  const handleApplyFilters = () => {
    onFiltersChange(localFilters);
    onClose();
  };

  const handleResetFilters = () => {
    const resetFilters = {
      claimType: 'all',
      verdictStatus: 'all',
      country: 'all',
      dateRange: 'all',
      efcsnVerified: false,
      highConfidence: false,
      hasEvidence: false
    };
    setLocalFilters(resetFilters);
    onFiltersChange(resetFilters);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/20 z-40 lg:hidden"
        onClick={onClose}
      />
      {/* Filter Panel */}
      <div className={`
        fixed lg:relative top-0 right-0 h-full lg:h-auto w-80 lg:w-full
        bg-card border-l lg:border lg:rounded-lg border-border shadow-elevated lg:shadow-soft
        z-50 lg:z-auto transform transition-transform duration-300
        ${isOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full lg:h-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <h3 className="text-lg font-semibold text-foreground">Filters</h3>
            <Button
              variant="ghost"
              size="icon"
              iconName="X"
              onClick={onClose}
              className="lg:hidden"
            />
          </div>

          {/* Filter Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            {/* Claim Type */}
            <div>
              <Select
                label="Claim Type"
                options={claimTypeOptions}
                value={localFilters?.claimType}
                onChange={(value) => handleFilterChange('claimType', value)}
              />
            </div>

            {/* Verdict Status */}
            <div>
              <Select
                label="Verdict Status"
                options={verdictStatusOptions}
                value={localFilters?.verdictStatus}
                onChange={(value) => handleFilterChange('verdictStatus', value)}
              />
            </div>

            {/* Country */}
            <div>
              <Select
                label="Country/Region"
                options={countryOptions}
                value={localFilters?.country}
                onChange={(value) => handleFilterChange('country', value)}
                searchable
              />
            </div>

            {/* Date Range */}
            <div>
              <Select
                label="Date Range"
                options={dateRangeOptions}
                value={localFilters?.dateRange}
                onChange={(value) => handleFilterChange('dateRange', value)}
              />
            </div>

            {/* Quality Filters */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-foreground">Quality Filters</h4>
              
              <Checkbox
                label="EFCSN Verified"
                description="Claims verified by European Fact-Checking Standards Network"
                checked={localFilters?.efcsnVerified}
                onChange={(e) => handleFilterChange('efcsnVerified', e?.target?.checked)}
              />

              <Checkbox
                label="High Confidence (>80%)"
                description="Claims with high confidence scores"
                checked={localFilters?.highConfidence}
                onChange={(e) => handleFilterChange('highConfidence', e?.target?.checked)}
              />

              <Checkbox
                label="Has Evidence"
                description="Claims with attached evidence and sources"
                checked={localFilters?.hasEvidence}
                onChange={(e) => handleFilterChange('hasEvidence', e?.target?.checked)}
              />
            </div>
          </div>

          {/* Footer Actions */}
          <div className="p-4 border-t border-border space-y-2">
            <Button
              variant="default"
              fullWidth
              onClick={handleApplyFilters}
            >
              Apply Filters
            </Button>
            <Button
              variant="outline"
              fullWidth
              onClick={handleResetFilters}
            >
              Reset All
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default FilterPanel;